/* eslint-disable no-shadow */
/* eslint-disable class-methods-use-this */
/* eslint-disable max-classes-per-file */

import { Core, WebViewerInstance } from '@pdftron/webviewer';
import { WebViewerWrapper, PDFManagerFactory } from 'pdftron';
import store from 'store/store';
import axiosInstanceQueries from 'store/queries/queriesAxios';
import {
  AnnotationCustomData,
  DocumentTypes,
  InputAnnotation,
  ManualAlignZoneCustomData,
  OPPOSITE_DOCUMENT,
  PDFTRON_DEFAULT_TOOL,
  SerializedManualAlignZone,
} from 'types';
import { AnnotationAction } from 'components/PDFViewer/Utils';
import { app, getTransparencyOpacity, getNextManualAlignZoneId } from 'store';
import { inspection } from 'store';
import AnnotationMixin from 'pdftron/docManager/Annotation';
import GVAnnotationMixin from './utils/GVAnnotationMixin';
import { PREP_TOOL_LABEL, PREP_TOOL_ANNOTATION } from './utils/annotationConstants';
import { getPageRotation } from './utils/labelPositionUtils';
import drawMarkupLabel from './utils/drawMarkupLabel';
import {
  updateCropAndPolygonOverlays,
  setCursorStyle,
  CursorStyle,
  getIframeDocument,
  isPagePointInsideCropZone,
  resetCursors,
} from './utils/CropToolUtils';
import { useDocumentToastStateStore } from 'zstore/documentToastStateStore';
import PDFTronManager from 'components/PDFViewer/PDFTronManager';
import { trackEvent } from 'components/Tracker/TrackerProvider';
import { getInspectionId } from 'store/inspection/inspectionSelectors';
import { setActiveResizeHandle } from './AnchorResizeTool';
import {
  aspectRatioToggleTrackEvent,
  aspectRatioUnlockedDataValue,
  capsuleBoundsFromHandleRect,
  cornerHandleSize,
  edgeHandleSize,
  manualAlignResizeHandlePoints,
  serializeManualAlignZone,
  type HandleRect,
} from './manualAlignZoneData';

const UNLOCKED_BORDER_DASH_LENGTH = 6;
const UNLOCKED_BORDER_DASH_GAP = 4;

function isAspectRatioUnlocked(annotation: { getCustomData: (key: string) => string }): boolean {
  return annotation.getCustomData(ManualAlignZoneCustomData.aspectRatioUnlocked) === 'true';
}

// Cached anchor icon image - exported for CropZone to draw anchor when crop is active in manual align mode
let anchorIconImage: HTMLImageElement | null = null;
const anchorIconImg = new Image();
anchorIconImg.onload = () => {
  anchorIconImage = anchorIconImg;
};
anchorIconImg.src = '/icons/anchor-icon.svg';

export function drawAnchorIcon(ctx: CanvasRenderingContext2D, x: number, y: number) {
  const scale = 0.5;
  const iconSize = 16 * scale;
  const offsetX = x - iconSize / 2;
  const offsetY = y - iconSize / 2;
  ctx.save();
  if (anchorIconImage && anchorIconImage.complete && anchorIconImage.naturalWidth > 0) {
    ctx.drawImage(anchorIconImage, offsetX, offsetY, iconSize, iconSize);
  }
  ctx.restore();
}

let _anchorCursorCache: { zoom: number; cursor: string } | null = null;

function generateAnchorCursor(zoomLevel: number): string {
  if (_anchorCursorCache && _anchorCursorCache.zoom === zoomLevel) {
    return _anchorCursorCache.cursor;
  }

  const baseSize = 8; // matches drawAnchorIcon: 16 * 0.5
  const size = Math.max(8, Math.min(128, Math.round(baseSize * zoomLevel)));
  const hotspot = Math.round(size / 2);

  const cursor = `url("data:image/svg+xml,${encodeURIComponent(
    `<svg xmlns='http://www.w3.org/2000/svg' width='${size}' height='${size}' viewBox='0 0 16 16' fill='none'>` +
      "<path d='M8 5.33325V10.6666' stroke='black' stroke-linecap='round' stroke-linejoin='round'/>" +
      "<path d='M1.79978 6.86661C1.65041 7.01583 1.53192 7.19302 1.45107 7.38806C1.37023 7.58309 1.32861 7.79215 1.32861 8.00328C1.32861 8.21441 1.37023 8.42347 1.45107 8.61851C1.53192 8.81354 1.65041 8.99073 1.79978 9.13995L6.85978 14.1999C7.00899 14.3493 7.18618 14.4678 7.38122 14.5487C7.57626 14.6295 7.78531 14.6711 7.99644 14.6711C8.20757 14.6711 8.41663 14.6295 8.61167 14.5487C8.80671 14.4678 8.9839 14.3493 9.13311 14.1999L14.1931 9.13995C14.3425 8.99073 14.461 8.81354 14.5418 8.61851C14.6227 8.42347 14.6643 8.21441 14.6643 8.00328C14.6643 7.79215 14.6227 7.58309 14.5418 7.38806C14.461 7.19302 14.3425 7.01583 14.1931 6.86661L9.13311 1.80661C8.9839 1.65725 8.80671 1.53875 8.61167 1.45791C8.41663 1.37706 8.20757 1.33545 7.99644 1.33545C7.78531 1.33545 7.57626 1.37706 7.38122 1.45791C7.18618 1.53875 7.00899 1.65725 6.85978 1.80661L1.79978 6.86661Z' stroke='black' stroke-linecap='round' stroke-linejoin='round'/>" +
      "<path d='M5.3335 8H10.6668' stroke='black' stroke-linecap='round' stroke-linejoin='round'/>" +
      '</svg>',
  )
    .replace(/'/g, '%27')
    .replace(/"/g, '%22')}") ${hotspot} ${hotspot}, pointer`;

  _anchorCursorCache = { zoom: zoomLevel, cursor };
  return cursor;
}

// Spacebar pan support and dynamic cursor management for manual align mode
let isPanningWithSpacebar = false;
let lastSpacebarReleaseTime = 0;
let __spaceKeydownHandler: ((ev: KeyboardEvent) => void) | undefined;
let __spaceKeyupHandler: ((ev: KeyboardEvent) => void) | undefined;
let __blurHandler: (() => void) | undefined;
let __cursorSourceHandler: ((ev: MouseEvent) => void) | undefined;
let __cursorTargetHandler: ((ev: MouseEvent) => void) | undefined;

function getScrollElements() {
  const pdfDocManager = PDFManagerFactory.getPDFDocManager();
  return {
    source: pdfDocManager
      ?.getInstance(DocumentTypes.source)
      ?.instance?.Core.documentViewer.getScrollViewElement() as HTMLElement | null,
    target: pdfDocManager
      ?.getInstance(DocumentTypes.target)
      ?.instance?.Core.documentViewer.getScrollViewElement() as HTMLElement | null,
  };
}

function togglePanningClass(enable: boolean) {
  const { source, target } = getScrollElements();
  const method = enable ? 'add' : 'remove';
  source?.classList[method]('manual-align-panning');
  target?.classList[method]('manual-align-panning');
}

function handleManualAlignCursorMove(documentType: DocumentTypes, e: MouseEvent) {
  if (isPanningWithSpacebar) return;

  const state = store.getState();
  const manualAlignMode = state.inspection.manualAlignMode;
  if (!manualAlignMode.isActive) return;

  const pdfDocManager = PDFManagerFactory.getPDFDocManager();
  const wrapper = pdfDocManager?.getInstance(documentType);
  if (!wrapper?.instance) return;

  const docViewer = wrapper.instance.Core.documentViewer;
  const displayMode = docViewer.getDisplayModeManager().getDisplayMode();
  const scrollViewElement = docViewer.getScrollViewElement();
  const scrollRect = scrollViewElement.getBoundingClientRect();
  const windowX = e.clientX - scrollRect.left + scrollViewElement.scrollLeft;
  const windowY = e.clientY - scrollRect.top + scrollViewElement.scrollTop;
  const windowPoint = { x: windowX, y: windowY };
  const page = displayMode.getSelectedPages(windowPoint, windowPoint).first;
  if (!page) return;

  const iframeDoc = getIframeDocument(wrapper, page);
  if (!iframeDoc) return;

  const originDocType = (manualAlignMode.originZoneDocumentType ?? DocumentTypes.source) as DocumentTypes;
  const overlayDocType = OPPOSITE_DOCUMENT[originDocType];
  const isPlacingMaster = !manualAlignMode.masterAnchorPoint;

  const isClickable = isPlacingMaster
    ? documentType === originDocType &&
      (() => {
        const cropZone = wrapper.instance.Core.annotationManager.getAnnotationById(manualAlignMode.cropZoneId);
        const pagePoint = displayMode.windowToPage(windowPoint, page);
        const zoomLevel = wrapper.instance.UI.getZoomLevel();
        return (
          !!cropZone && cropZone.PageNumber === page && isPagePointInsideCropZone(pagePoint, cropZone, zoomLevel, false)
        );
      })()
    : documentType === overlayDocType;

  const zoomLevel = wrapper.instance.UI.getZoomLevel();
  setCursorStyle(isClickable ? generateAnchorCursor(zoomLevel) : CursorStyle.NOT_ALLOWED, iframeDoc);
}

function setupManualAlignListeners() {
  teardownManualAlignListeners();

  const keydownHandler = (ev: KeyboardEvent) => {
    const key = (ev as any).code || (ev as any).key;
    if (key === 'Space' || key === ' ') {
      ev.preventDefault();
      ev.stopPropagation();
      if (isPanningWithSpacebar) return;
      isPanningWithSpacebar = true;
      togglePanningClass(true);
    }
  };

  const keyupHandler = (ev: KeyboardEvent) => {
    const key = (ev as any).code || (ev as any).key;
    if (key === 'Space' || key === ' ') {
      ev.preventDefault();
      ev.stopPropagation();
      isPanningWithSpacebar = false;
      lastSpacebarReleaseTime = Date.now();
      togglePanningClass(false);
    }
  };

  __spaceKeydownHandler = keydownHandler;
  __spaceKeyupHandler = keyupHandler;

  const blurHandler = () => {
    if (!isPanningWithSpacebar) return;
    isPanningWithSpacebar = false;
    lastSpacebarReleaseTime = Date.now();
    togglePanningClass(false);
  };
  __blurHandler = blurHandler;

  const sourceWin = PDFTronManager.getIframeWindow(DocumentTypes.source);
  const targetWin = PDFTronManager.getIframeWindow(DocumentTypes.target);
  sourceWin?.addEventListener('keydown', keydownHandler, { capture: true } as any);
  sourceWin?.addEventListener('keyup', keyupHandler, { capture: true } as any);
  sourceWin?.addEventListener('blur', blurHandler as any);
  if (targetWin && targetWin !== sourceWin) {
    targetWin.addEventListener('keydown', keydownHandler, { capture: true } as any);
    targetWin.addEventListener('keyup', keyupHandler, { capture: true } as any);
    targetWin.addEventListener('blur', blurHandler as any);
  }

  const { source: sourceScrollEl, target: targetScrollEl } = getScrollElements();
  const sourceHandler = (e: MouseEvent) => handleManualAlignCursorMove(DocumentTypes.source, e);
  const targetHandler = (e: MouseEvent) => handleManualAlignCursorMove(DocumentTypes.target, e);
  __cursorSourceHandler = sourceHandler;
  __cursorTargetHandler = targetHandler;
  sourceScrollEl?.addEventListener('mousemove', sourceHandler as any);
  targetScrollEl?.addEventListener('mousemove', targetHandler as any);
}

function teardownManualAlignListeners() {
  if (__spaceKeydownHandler) {
    const sourceWin = PDFTronManager.getIframeWindow(DocumentTypes.source);
    const targetWin = PDFTronManager.getIframeWindow(DocumentTypes.target);
    sourceWin?.removeEventListener('keydown', __spaceKeydownHandler, { capture: true } as any);
    sourceWin?.removeEventListener('keyup', __spaceKeyupHandler!, { capture: true } as any);
    sourceWin?.removeEventListener('blur', __blurHandler! as any);
    if (targetWin && targetWin !== sourceWin) {
      targetWin.removeEventListener('keydown', __spaceKeydownHandler, { capture: true } as any);
      targetWin.removeEventListener('keyup', __spaceKeyupHandler!, { capture: true } as any);
      targetWin.removeEventListener('blur', __blurHandler! as any);
    }
  }
  if (__cursorSourceHandler) {
    const { source: sourceScrollEl, target: targetScrollEl } = getScrollElements();
    sourceScrollEl?.removeEventListener('mousemove', __cursorSourceHandler as any);
    targetScrollEl?.removeEventListener('mousemove', __cursorTargetHandler as any);
  }
  __spaceKeydownHandler = undefined;
  __spaceKeyupHandler = undefined;
  __blurHandler = undefined;
  __cursorSourceHandler = undefined;
  __cursorTargetHandler = undefined;
  isPanningWithSpacebar = false;
  resetCursors();
}

// Move-attempt detection for anchored target zones
let __moveAttemptSourceHandler: ((ev: PointerEvent) => void) | undefined;
let __moveAttemptTargetHandler: ((ev: PointerEvent) => void) | undefined;
let _lastMoveToastTime = 0;
const MOVE_TOAST_COOLDOWN_MS = 5000;

function handleMoveAttemptPointerDown(documentType: DocumentTypes, e: PointerEvent) {
  const pdfDocManager = PDFManagerFactory.getPDFDocManager();
  const wrapper = pdfDocManager?.getInstance(documentType);
  if (!wrapper?.instance) return;

  const annotationManager = wrapper.instance.Core.annotationManager;
  const selectedAnnotations = annotationManager.getSelectedAnnotations();

  const selectedTargetZone = selectedAnnotations.find(
    (a) =>
      a.getCustomData(ManualAlignZoneCustomData.manualAlignZoneAnnotation) === 'true' &&
      a.getCustomData(ManualAlignZoneCustomData.linkedZoneId) &&
      a.getCustomData(ManualAlignZoneCustomData.linkedZoneId) !== a.Id,
  );
  if (!selectedTargetZone) return;

  const docViewer = wrapper.instance.Core.documentViewer;
  const displayMode = docViewer.getDisplayModeManager().getDisplayMode();
  const scrollViewElement = docViewer.getScrollViewElement();
  const scrollRect = scrollViewElement.getBoundingClientRect();
  const windowX = e.clientX - scrollRect.left + scrollViewElement.scrollLeft;
  const windowY = e.clientY - scrollRect.top + scrollViewElement.scrollTop;
  const windowPoint = { x: windowX, y: windowY };
  const page = displayMode.getSelectedPages(windowPoint, windowPoint).first;
  if (!page || page !== selectedTargetZone.PageNumber) return;

  const pagePoint = displayMode.windowToPage(windowPoint, page);

  const { X, Y, Width, Height } = selectedTargetZone;
  if (pagePoint.x < X || pagePoint.x > X + Width || pagePoint.y < Y || pagePoint.y > Y + Height) return;

  const zoomLevel = wrapper.instance.UI.getZoomLevel();
  const handleThreshold = 16 / zoomLevel;
  const resizeHandles = manualAlignResizeHandlePoints(
    { X, Y, Width, Height },
    isAspectRatioUnlocked(selectedTargetZone),
  );
  const isNearResizeHandle = resizeHandles.some(
    (handle) =>
      Math.abs(pagePoint.x - handle.x) < handleThreshold && Math.abs(pagePoint.y - handle.y) < handleThreshold,
  );
  if (isNearResizeHandle) return;

  const now = Date.now();
  if (now - _lastMoveToastTime < MOVE_TOAST_COOLDOWN_MS) return;
  _lastMoveToastTime = now;

  const { setDocumentToast } = useDocumentToastStateStore.getState();
  setDocumentToast(documentType, {
    type: 'info',
    message: 'This zone is anchored and cannot be moved. You can resize it, or use Edit Anchor to change its position.',
    duration: 5000,
  });
}

function setupMoveAttemptListeners() {
  teardownMoveAttemptListeners();
  const { source: sourceScrollEl, target: targetScrollEl } = getScrollElements();
  const sourceHandler = (e: PointerEvent) => handleMoveAttemptPointerDown(DocumentTypes.source, e);
  const targetHandler = (e: PointerEvent) => handleMoveAttemptPointerDown(DocumentTypes.target, e);
  __moveAttemptSourceHandler = sourceHandler;
  __moveAttemptTargetHandler = targetHandler;
  sourceScrollEl?.addEventListener('pointerdown', sourceHandler as any, true);
  targetScrollEl?.addEventListener('pointerdown', targetHandler as any, true);
}

function teardownMoveAttemptListeners() {
  if (__moveAttemptSourceHandler) {
    const { source: sourceScrollEl, target: targetScrollEl } = getScrollElements();
    sourceScrollEl?.removeEventListener('pointerdown', __moveAttemptSourceHandler as any, true);
    targetScrollEl?.removeEventListener('pointerdown', __moveAttemptTargetHandler as any, true);
  }
  __moveAttemptSourceHandler = undefined;
  __moveAttemptTargetHandler = undefined;
}

interface ManualAlignZoneDrawContext {
  X: number;
  Y: number;
  Width: number;
  Height: number;
  PageNumber: number;
  getCustomData: (key: string) => any;
}

interface NormalizedAnchorPoint {
  x: number;
  y: number;
}

interface PageAnchorPoint {
  x: number;
  y: number;
}

interface OverlayBounds {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

function normalizeQuarterTurns(rotation: number): number {
  return ((rotation % 4) + 4) % 4;
}

function rotatePointByQuarterTurns(point: PageAnchorPoint, quarterTurns: number): PageAnchorPoint {
  switch (normalizeQuarterTurns(quarterTurns)) {
    case 1:
      return { x: -point.y, y: point.x };
    case 2:
      return { x: -point.x, y: -point.y };
    case 3:
      return { x: point.y, y: -point.x };
    default:
      return point;
  }
}

function getRotatedOverlayBounds(
  width: number,
  height: number,
  sourceAnchorPoint: NormalizedAnchorPoint,
  targetAnchor: PageAnchorPoint,
  rotationQuarterTurns: number,
  scale: number = 1,
): OverlayBounds {
  const scaledWidth = width * scale;
  const scaledHeight = height * scale;
  const anchorLocalX = sourceAnchorPoint.x * scaledWidth;
  const anchorLocalY = sourceAnchorPoint.y * scaledHeight;
  const corners = [
    { x: -anchorLocalX, y: -anchorLocalY },
    { x: scaledWidth - anchorLocalX, y: -anchorLocalY },
    { x: scaledWidth - anchorLocalX, y: scaledHeight - anchorLocalY },
    { x: -anchorLocalX, y: scaledHeight - anchorLocalY },
  ].map((corner) => rotatePointByQuarterTurns(corner, rotationQuarterTurns));

  const xs = corners.map((corner) => corner.x);
  const ys = corners.map((corner) => corner.y);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);

  return {
    x1: targetAnchor.x + minX,
    y1: targetAnchor.y + minY,
    x2: targetAnchor.x + maxX,
    y2: targetAnchor.y + maxY,
  };
}

function getOverlayDrawDimensions(
  overlayWidth: number,
  overlayHeight: number,
  rotationQuarterTurns: number,
): { width: number; height: number } {
  return normalizeQuarterTurns(rotationQuarterTurns) % 2 === 0
    ? { width: overlayWidth, height: overlayHeight }
    : { width: overlayHeight, height: overlayWidth };
}

function getOverlayRotationQuarterTurns(
  sourceInstance: WebViewerInstance,
  sourcePageNumber: number,
  targetInstance: WebViewerInstance,
  targetPageNumber: number,
): number {
  const sourceRotation = getPageRotation(sourceInstance, sourcePageNumber);
  const targetRotation = getPageRotation(targetInstance, targetPageNumber);
  return normalizeQuarterTurns(sourceRotation - targetRotation);
}

function drawManualAlignZoneCommon(
  ctx: CanvasRenderingContext2D,
  instance: WebViewerInstance,
  annotation: ManualAlignZoneDrawContext,
  isSelected: boolean,
  isHovering: boolean,
  zoomLevel: number,
) {
  const { SELECTED_FILL_STYLE, DEFAULT_FILL_STYLE } = PREP_TOOL_ANNOTATION;
  const annotationRectangle = new Path2D();
  annotationRectangle.rect(annotation.X, annotation.Y, annotation.Width, annotation.Height);
  ctx.save();
  ctx.strokeStyle = isSelected ? SELECTED_FILL_STYLE : '#EA3632';
  ctx.lineWidth = 1.5;
  if (isAspectRatioUnlocked(annotation)) {
    const zoom = zoomLevel > 0 ? zoomLevel : 1;
    const dashLength = UNLOCKED_BORDER_DASH_LENGTH / zoom;
    const dashGap = UNLOCKED_BORDER_DASH_GAP / zoom;
    ctx.setLineDash([dashLength, dashGap]);
  }
  ctx.stroke(annotationRectangle);
  ctx.restore();
  ctx.fillStyle = 'rgba(255, 255, 255, 0.01)';
  ctx.fill(annotationRectangle);

  const anchorPointData = annotation.getCustomData('anchorPoint');
  if (anchorPointData && anchorPointData !== 'none') {
    try {
      const anchorPoint = JSON.parse(anchorPointData);
      const anchorX = annotation.X + anchorPoint.x * annotation.Width;
      const anchorY = annotation.Y + anchorPoint.y * annotation.Height;
      drawAnchorIcon(ctx, anchorX, anchorY);
    } catch {
      console.error('Invalid anchor point');
    }
  }

  if (isSelected || isHovering) {
    const realPageRotation = getPageRotation(instance, annotation.PageNumber);
    const { HEIGHT, WIDTH_PADDING, GAP } = PREP_TOOL_LABEL;
    const fillStyle = isSelected ? SELECTED_FILL_STYLE : DEFAULT_FILL_STYLE;
    const zoneNumber = annotation.getCustomData(ManualAlignZoneCustomData.zoneNumber);
    const labelText = zoneNumber ? `Align to Match Zone: ${zoneNumber}` : 'Align to Match Zone';
    const pageWidth = instance.Core.documentViewer.getPageWidth(annotation.PageNumber);
    const pageHeight = instance.Core.documentViewer.getPageHeight(annotation.PageNumber);
    drawMarkupLabel(
      ctx,
      labelText,
      annotation.X,
      annotation.Y,
      HEIGHT,
      GAP,
      WIDTH_PADDING,
      fillStyle,
      zoomLevel,
      false,
      annotation.Width,
      annotation.Height,
      realPageRotation,
      pageWidth,
      pageHeight,
    );
  }
}

interface ManualAlignZoneAnnotation extends Core.Annotations.Annotation {
  cleanup?(): void;
}

enum ManualAlignZoneType {
  Origin = 'origin',
  Overlay = 'overlay',
}

class ManualAlignZone {
  source: WebViewerWrapper | null = null;
  target: WebViewerWrapper | null = null;

  private targetAnchorPointAnnotation: Core.Annotations.Annotation | null = null;
  private masterAnchorPointAnnotation: Core.Annotations.Annotation | null = null;
  private prefetchedPageImage: Promise<{ blob: Blob; dpiUsed: number }> | null = null;
  private currentOriginZoneDocumentType: DocumentTypes | null = null;
  private skipCascadeDelete = false;
  // Set here, read by CropZone.cropZoneAnnotationChanged via mixin - prevents exitManualAlignMode when we programmatically delete crop during conversion
  private skipCropDeleteExitManualAlign = false;
  private editModeRestoreSnapshot: {
    originZoneId: string;
    originAnchorPoint: string;
    overlayZones: SerializedManualAlignZone[];
  } | null = null;
  private exitManualAlignInProgress = false;

  constructor(source: WebViewerWrapper | null, target: WebViewerWrapper | null) {
    this.source = source;
    this.target = target;
  }

  get annotationMixin() {
    return new AnnotationMixin(this.source, this.target);
  }

  private refreshPageOverlayForManualAlign(documentType: DocumentTypes, pageNumber: number, refresh = true): void {
    const documentWrapper = PDFManagerFactory.getViewer(documentType);
    if (!documentWrapper) return;
    updateCropAndPolygonOverlays(documentWrapper.instance, pageNumber, refresh);
  }

  createSourceManualAlignZoneAnnotation(instance: WebViewerInstance) {
    class SourceManualAlignZoneAnnotation extends GVAnnotationMixin(instance.Core.Annotations.RectangleAnnotation) {
      constructor() {
        super();
        this.setCustomData(ManualAlignZoneCustomData.manualAlignZoneAnnotation, 'true');
        this.ReadOnly = true;
        this.IsHoverable = true;
        this.disableRotationControl();
      }

      draw(ctx: CanvasRenderingContext2D, pageMatrix: any, pageRotation: number, zoomLevel: number) {
        this.setStyles(ctx, pageMatrix);
        const isSelected = instance.Core.annotationManager.getSelectedAnnotations().some((a) => a.Id === this.Id);
        const isHovering = (this as any)?.IsHovering === true;
        drawManualAlignZoneCommon(ctx, instance, this, isSelected, isHovering, zoomLevel);
      }
    }
    return SourceManualAlignZoneAnnotation;
  }

  createTargetManualAlignZoneAnnotation(
    instance: WebViewerInstance,
    overlayImageBitmap: ImageBitmap,
    sourceAnchorPoint: NormalizedAnchorPoint,
    rotationQuarterTurns: number,
  ) {
    class TargetZoneSelectionModel extends (instance.Core.Annotations as any).SelectionModel {
      constructor(annotation: { getCustomData: (key: string) => string }, canModify: boolean) {
        super(annotation, canModify);
        if (!canModify) return;

        const controlHandles = this.getControlHandles();
        const BoxControlHandle = (instance.Core.Annotations as any).BoxControlHandle;
        const pushHandle = (
          horizontalAlignment: number,
          verticalAlignment: number,
          handle?: 'width-edge' | 'height-edge',
        ) => {
          const size = handle ? edgeHandleSize(handle) : { width: cornerHandleSize(), height: cornerHandleSize() };
          const control = new BoxControlHandle(size.width, size.height, horizontalAlignment, verticalAlignment);
          if (handle && typeof control.move === 'function') {
            const originalMove = control.move.bind(control);
            control.move = (annot: object, ...args: unknown[]) => {
              setActiveResizeHandle(annot, handle);
              return originalMove(annot, ...args);
            };
          }
          if (handle && typeof control.draw === 'function' && typeof control.getDimensions === 'function') {
            const originalDraw = control.draw;
            control.draw = function drawEdgeCapsule(
              this: {
                getDimensions: (annotationToDraw: unknown, selectionBox: unknown, zoom: number) => HandleRect;
              },
              ctx: CanvasRenderingContext2D,
              annotationToDraw: unknown,
              selectionBox: unknown,
              zoom: number,
            ) {
              const bounds = capsuleBoundsFromHandleRect(this.getDimensions(annotationToDraw, selectionBox, zoom));
              if (!bounds) {
                originalDraw.call(this, ctx, annotationToDraw, selectionBox, zoom);
                return;
              }
              const radius = Math.min(bounds.width, bounds.height) / 2;
              const color = BoxControlHandle.color?.toString?.() ?? 'rgb(49, 131, 200)';
              const outline = BoxControlHandle.outlineColor?.toString?.() ?? color;
              const outlineThickness = BoxControlHandle.selectionPointOutlineThickness ?? 2;
              ctx.save();
              // Corner handles leave a drop shadow on this context. A capsule stroke
              // picks that shadow up as a second edge, so it is cleared before the pill is drawn.
              ctx.shadowColor = 'transparent';
              ctx.shadowBlur = 0;
              ctx.shadowOffsetX = 0;
              ctx.shadowOffsetY = 0;
              ctx.setLineDash([]);
              ctx.fillStyle = color;
              ctx.strokeStyle = outline;
              ctx.lineWidth = outlineThickness / (zoom > 0 ? zoom : 1);
              ctx.beginPath();
              ctx.roundRect(bounds.x, bounds.y, bounds.width, bounds.height, radius);
              ctx.fill();
              ctx.stroke();
              ctx.restore();
            };
          }
          controlHandles.push(control);
        };

        pushHandle(1, 16); // top-left
        pushHandle(4, 16); // top-right
        pushHandle(1, 64); // bottom-left
        pushHandle(4, 64); // bottom-right
        if (isAspectRatioUnlocked(annotation)) {
          pushHandle(2, 16, 'height-edge'); // top
          pushHandle(2, 64, 'height-edge'); // bottom
          pushHandle(1, 32, 'width-edge'); // left
          pushHandle(4, 32, 'width-edge'); // right
        }
      }
    }

    class TargetManualAlignZoneAnnotation extends GVAnnotationMixin(instance.Core.Annotations.RectangleAnnotation) {
      constructor() {
        super();
        this.setCustomData(ManualAlignZoneCustomData.manualAlignZoneAnnotation, 'true');
        this.ReadOnly = false;
        this.NoMove = false;
        this.IsHoverable = true;
        this.disableRotationControl();
        (this as any).selectionModel = TargetZoneSelectionModel;
      }

      draw(ctx: CanvasRenderingContext2D, pageMatrix: any, pageRotation: number, zoomLevel: number) {
        this.setStyles(ctx, pageMatrix);
        const isSelected = instance.Core.annotationManager.getSelectedAnnotations().some((a) => a.Id === this.Id);
        const isHovering = (this as any)?.IsHovering === true;

        if ((isHovering || isSelected) && overlayImageBitmap) {
          const overlayAnchorPointData = this.getCustomData('anchorPoint');
          let overlayAnchorPoint: NormalizedAnchorPoint = { x: 0.5, y: 0.5 };
          try {
            if (overlayAnchorPointData && overlayAnchorPointData !== 'none') {
              overlayAnchorPoint = JSON.parse(overlayAnchorPointData);
            }
          } catch {
            console.error('Invalid anchor point');
          }

          const anchorX = this.X + overlayAnchorPoint.x * this.Width;
          const anchorY = this.Y + overlayAnchorPoint.y * this.Height;
          const drawDimensions = getOverlayDrawDimensions(this.Width, this.Height, rotationQuarterTurns);
          const sourceAnchorX = sourceAnchorPoint.x * drawDimensions.width;
          const sourceAnchorY = sourceAnchorPoint.y * drawDimensions.height;

          ctx.save();
          ctx.globalAlpha = getTransparencyOpacity(store.getState());
          ctx.globalCompositeOperation = 'source-over';
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high';
          ctx.translate(anchorX, anchorY);
          ctx.rotate(normalizeQuarterTurns(rotationQuarterTurns) * (Math.PI / 2));
          ctx.drawImage(
            overlayImageBitmap,
            -sourceAnchorX,
            -sourceAnchorY,
            drawDimensions.width,
            drawDimensions.height,
          );
          ctx.restore();
        }

        drawManualAlignZoneCommon(ctx, instance, this, isSelected, isHovering, zoomLevel);
      }

      cleanup(): void {
        try {
          if (overlayImageBitmap) {
            overlayImageBitmap.close();
          }
        } catch {
          // ignore
        }
      }
    }
    return TargetManualAlignZoneAnnotation;
  }

  showSnackMessage(message: string, type: 'error' | 'success' | 'info' = 'info'): void {
    if (type === 'error') {
      console.error(message);
      store.dispatch(
        app.actions.setSnackMessage({
          message: 'Something went wrong. Please try again.',
          type,
        }),
      );
    } else {
      store.dispatch(
        app.actions.setSnackMessage({
          message,
          type,
        }),
      );
    }
  }

  private applyManualAlignModeStyles(scrollElement: HTMLElement | null, enabled: boolean): void {
    if (!scrollElement) return;
    if (enabled) {
      scrollElement.classList.add('manual-align-mode');
    } else {
      scrollElement.classList.remove('manual-align-mode');
      scrollElement.classList.remove('manual-align-panning');
    }
  }

  setupManualAlignModeUI(originZoneDocumentType: DocumentTypes | null): void {
    store.dispatch(inspection.actions.setSelectedTool({ tool: PDFTRON_DEFAULT_TOOL }));
    this.source?.instance.UI.setToolMode(PDFTRON_DEFAULT_TOOL);
    this.target?.instance.UI.setToolMode(PDFTRON_DEFAULT_TOOL);
    const { source: sourceScrollEl, target: targetScrollEl } = getScrollElements();
    this.applyManualAlignModeStyles(sourceScrollEl, true);
    this.applyManualAlignModeStyles(targetScrollEl, true);
    setupManualAlignListeners();

    // Hide internal (non-GV) annotations so that link/action annotations
    // embedded in the PDF don't intercept anchor placement clicks
    this.annotationMixin.hideOrShowInternalAnnotations(false);

    this.source?.removeAnnotationDocumentHighlight();
    this.target?.removeAnnotationDocumentHighlight();
    const originDocType = originZoneDocumentType ?? DocumentTypes.source;
    const originWrapper = originDocType === DocumentTypes.source ? this.source : this.target;
    originWrapper?.addDocumentHighlight();
  }

  enterManualAlignMode(
    instance: WebViewerInstance,
    annotation: Core.Annotations.Annotation,
    originZoneDocumentType: DocumentTypes,
  ) {
    this.currentOriginZoneDocumentType = originZoneDocumentType;
    store.dispatch(
      inspection.actions.enterManualAlignMode({
        cropZoneId: annotation.Id,
        pageNumber: annotation.PageNumber,
        originZoneDocumentType,
      }),
    );
    annotation.NoMove = true;
    annotation.NoResize = true;
    instance.Core.annotationManager.redrawAnnotation(annotation);

    this.setupManualAlignModeUI(originZoneDocumentType);
    const { expireAllToast, setDocumentToast } = useDocumentToastStateStore.getState();
    expireAllToast();
    setDocumentToast(originZoneDocumentType, {
      type: 'escToExit',
      message: 'Place reference anchor point',
      infoMessage: 'Esc to exit',
    });

    // Prefetch the page image so it's ready when anchors are placed
    this.prefetchPageImage(annotation.PageNumber, originZoneDocumentType);
  }

  private prefetchPageImage(pageNumber: number, originZoneDocumentType: DocumentTypes): void {
    const state = store.getState();
    const file = state.files[originZoneDocumentType].file;
    const pagesBasePath = file.fileMetadata?.pagesBasePath;

    if (!pagesBasePath) {
      this.prefetchedPageImage = null;
      return;
    }

    this.prefetchedPageImage = axiosInstanceQueries
      .get(`files/${file.fileId}/page-image-url?page=${pageNumber}`)
      .then(async (response) => {
        const imageResponse = await fetch(response.data.url);
        if (!imageResponse.ok) {
          throw new Error(`Failed to fetch image: ${imageResponse.status} ${imageResponse.statusText}`);
        }
        const blob = await imageResponse.blob();
        const dpiUsed: number = response.data.dpiUsed ?? 300;
        return { blob, dpiUsed };
      })
      .catch((error) => {
        console.error('Failed to prefetch page image:', error);
        throw error;
      });
  }

  private cleanupAnchorPointAnnotations(): void {
    const originZoneDocumentType = this.currentOriginZoneDocumentType ?? DocumentTypes.source;
    const overlayDocType = OPPOSITE_DOCUMENT[originZoneDocumentType];
    const masterWrapper = originZoneDocumentType === DocumentTypes.source ? this.source : this.target;
    const targetWrapper = overlayDocType === DocumentTypes.source ? this.source : this.target;

    if (this.targetAnchorPointAnnotation && targetWrapper?.annotationManager) {
      targetWrapper.annotationManager.deleteAnnotation(this.targetAnchorPointAnnotation, {
        imported: false,
        force: true,
      });
      this.targetAnchorPointAnnotation = null;
    }
    if (this.masterAnchorPointAnnotation && masterWrapper?.annotationManager) {
      masterWrapper.annotationManager.deleteAnnotation(this.masterAnchorPointAnnotation, {
        imported: false,
        force: true,
      });
      this.masterAnchorPointAnnotation = null;
    }
  }

  async exitManualAlignMode() {
    if (this.exitManualAlignInProgress) return;
    this.exitManualAlignInProgress = true;
    try {
      const manualAlignState = store.getState().inspection.manualAlignMode;

      if (this.editModeRestoreSnapshot) {
        const snapshot = this.editModeRestoreSnapshot;
        this.editModeRestoreSnapshot = null;
        try {
          await this.restoreFromEditSnapshot(snapshot);
        } catch (err) {
          console.error('Failed to restore from edit snapshot:', err);
          this.showSnackMessage('Failed to restore. Please try again.', 'error');
        }
      }
      this.cleanupAnchorPointAnnotations();
      if (manualAlignState.cropZoneId) {
        const originDocType = (manualAlignState.originZoneDocumentType ?? DocumentTypes.source) as DocumentTypes;
        const originWrapper = originDocType === DocumentTypes.source ? this.source : this.target;
        const cropAnnotation = originWrapper?.annotationManager.getAnnotationById(manualAlignState.cropZoneId);
        if (cropAnnotation) {
          cropAnnotation.NoMove = false;
          cropAnnotation.NoResize = false;
          originWrapper?.annotationManager.redrawAnnotation(cropAnnotation);
        }
      }
      store.dispatch(inspection.actions.exitManualAlignMode());
      this.currentOriginZoneDocumentType = null;
      this.prefetchedPageImage?.catch(() => {});
      this.prefetchedPageImage = null;
      useDocumentToastStateStore.getState().expireAllToast();
      const { source: sourceScrollEl, target: targetScrollEl } = getScrollElements();
      this.applyManualAlignModeStyles(sourceScrollEl, false);
      this.applyManualAlignModeStyles(targetScrollEl, false);
      teardownManualAlignListeners();
      this.source?.removeAnnotationDocumentHighlight();
      this.target?.removeAnnotationDocumentHighlight();

      // Re-sync internal annotation visibility with the current user preference,
      // which may have changed while manual align was active
      const currentVisibility = store.getState().inspection.internalAnnotationsVisibility;
      this.annotationMixin.hideOrShowInternalAnnotations(currentVisibility);

      this.source?.instance?.Core.documentViewer.refreshAll();
      this.target?.instance?.Core.documentViewer.refreshAll();
    } finally {
      this.exitManualAlignInProgress = false;
    }
  }

  async startManualAlign(
    masterAnchorPoint: { x: number; y: number; pageNumber: number },
    targetAnchorPoint: { x: number; y: number; pageNumber: number },
  ) {
    const MIN_MANUAL_ALIGN_SCALE = 1e-6;
    this.editModeRestoreSnapshot = null;
    let cropImageBitmap: ImageBitmap | null = null;
    try {
      const state = store.getState();
      const manualAlignMode = state.inspection.manualAlignMode;

      if (!manualAlignMode.isActive) {
        return;
      }

      if (!manualAlignMode.cropZoneId) {
        this.showSnackMessage('Origin zone ID is missing.', 'error');
        this.exitManualAlignMode();
        return;
      }

      const masterAnchor = manualAlignMode.masterAnchorPoint || masterAnchorPoint;
      const targetAnchor = manualAlignMode.targetAnchorPoint || targetAnchorPoint;

      if (!masterAnchor || !targetAnchor) {
        this.showSnackMessage('Both anchor points must be set.', 'error');
        this.exitManualAlignMode();
        return;
      }

      const originZoneDocumentType = (manualAlignMode.originZoneDocumentType ?? DocumentTypes.source) as DocumentTypes;
      const overlayDocumentType = OPPOSITE_DOCUMENT[originZoneDocumentType];

      const originWrapper = originZoneDocumentType === DocumentTypes.source ? this.source : this.target;
      const overlayWrapper = overlayDocumentType === DocumentTypes.source ? this.source : this.target;

      const originZoneAnnotation = originWrapper?.annotationManager.getAnnotationById(manualAlignMode.cropZoneId);
      if (!originZoneAnnotation) {
        this.showSnackMessage('Origin zone not found.', 'error');
        this.exitManualAlignMode();
        return;
      }

      const cropRect = originZoneAnnotation.getRect();
      const { x1, y1, x2, y2 } = cropRect;
      const pageNumber = originZoneAnnotation.PageNumber;
      const cropWidth = x2 - x1;
      const cropHeight = y2 - y1;
      const normalizedMasterX = (masterAnchor.x - x1) / cropWidth;
      const normalizedMasterY = (masterAnchor.y - y1) / cropHeight;
      const sourceAnchorPoint = { x: normalizedMasterX, y: normalizedMasterY };

      let imageBlob: Blob;
      let dpiUsed: number;
      try {
        if (!this.prefetchedPageImage) {
          throw new Error('Page image was not prefetched');
        }
        const result = await this.prefetchedPageImage;
        imageBlob = result.blob;
        dpiUsed = result.dpiUsed;
      } catch (error) {
        console.error('Failed to load page image:', error);
        this.showSnackMessage('Failed to load page image. Please try again.', 'error');
        this.exitManualAlignMode();
        return;
      }

      const scaleFactor = dpiUsed / 72;

      const imageX = x1 * scaleFactor;
      const imageY = y1 * scaleFactor;
      const imageWidthPixels = cropWidth * scaleFactor;
      const imageHeightPixels = cropHeight * scaleFactor;

      cropImageBitmap = await createImageBitmap(imageBlob, imageX, imageY, imageWidthPixels, imageHeightPixels);

      const overlayInstance = overlayWrapper?.instance;
      if (!overlayInstance || !overlayWrapper || !originWrapper?.instance) {
        this.showSnackMessage('Overlay document is not available.', 'error');
        this.exitManualAlignMode();
        if (cropImageBitmap) {
          try {
            cropImageBitmap.close();
          } catch {
            // ignore
          }
        }
        return;
      }

      const rotationQuarterTurns = getOverlayRotationQuarterTurns(
        originWrapper.instance,
        pageNumber,
        overlayInstance,
        targetAnchor.pageNumber,
      );

      let {
        x1: overlayX1,
        y1: overlayY1,
        x2: overlayX2,
        y2: overlayY2,
      } = getRotatedOverlayBounds(cropWidth, cropHeight, sourceAnchorPoint, targetAnchor, rotationQuarterTurns);

      const targetPageWidth = overlayInstance.Core.documentViewer.getPageWidth(targetAnchor.pageNumber) ?? 0;
      const targetPageHeight = overlayInstance.Core.documentViewer.getPageHeight(targetAnchor.pageNumber) ?? 0;

      // If the overlay exceeds page bounds, scale it down uniformly around the anchor
      // point so all resize handles remain accessible while the anchor stays locked.
      if (targetPageWidth > 0 && targetPageHeight > 0) {
        const anchorRelX = targetAnchor.x - overlayX1;
        const anchorRelY = targetAnchor.y - overlayY1;
        const rightRelX = overlayX2 - targetAnchor.x;
        const bottomRelY = overlayY2 - targetAnchor.y;
        let scale = 1;
        if (anchorRelX > 0 && overlayX1 < 0) scale = Math.min(scale, targetAnchor.x / anchorRelX);
        if (anchorRelY > 0 && overlayY1 < 0) scale = Math.min(scale, targetAnchor.y / anchorRelY);
        if (rightRelX > 0 && overlayX2 > targetPageWidth)
          scale = Math.min(scale, (targetPageWidth - targetAnchor.x) / rightRelX);
        if (bottomRelY > 0 && overlayY2 > targetPageHeight)
          scale = Math.min(scale, (targetPageHeight - targetAnchor.y) / bottomRelY);
        if (scale < 1) {
          scale = Number.isFinite(scale) ? Math.max(MIN_MANUAL_ALIGN_SCALE, scale) : MIN_MANUAL_ALIGN_SCALE;
          ({
            x1: overlayX1,
            y1: overlayY1,
            x2: overlayX2,
            y2: overlayY2,
          } = getRotatedOverlayBounds(
            cropWidth,
            cropHeight,
            sourceAnchorPoint,
            targetAnchor,
            rotationQuarterTurns,
            scale,
          ));
        }
      }

      let baseZone: Core.Annotations.Annotation;
      const isManualAlignZoneSource =
        originZoneAnnotation.getCustomData(ManualAlignZoneCustomData.manualAlignZoneAnnotation) === 'true';

      if (isManualAlignZoneSource) {
        baseZone = originZoneAnnotation;
        baseZone.setCustomData(ManualAlignZoneCustomData.originZoneDocumentType, originZoneDocumentType);
        // Update origin zone's anchor point to the new position the user placed
        baseZone.setCustomData('anchorPoint', JSON.stringify({ x: normalizedMasterX, y: normalizedMasterY }));
        originWrapper!.annotationManager.redrawAnnotation(baseZone);
        // Remove existing linked overlay zone and cleanup its ImageBitmap before creating a new one
        const existingLinkedId = baseZone.getCustomData(ManualAlignZoneCustomData.linkedZoneId) || baseZone.Id;
        const existingOverlays =
          overlayWrapper?.annotationManager
            .getAnnotationsList()
            .filter(
              (a) =>
                a.getCustomData(ManualAlignZoneCustomData.manualAlignZoneAnnotation) === 'true' &&
                a.getCustomData(ManualAlignZoneCustomData.linkedZoneId) === existingLinkedId,
            ) || [];
        for (const existingOverlay of existingOverlays) {
          this.cleanupAnnotationIfNeeded(existingOverlay);
          overlayWrapper!.annotationManager.deleteAnnotation(existingOverlay, { imported: false, force: true });
        }
      } else {
        const cropInstance = originWrapper!.instance!;
        const SourceManualAlignZoneClass = this.createSourceManualAlignZoneAnnotation(cropInstance);
        baseZone = new SourceManualAlignZoneClass();
        baseZone.setRect(cropRect);
        baseZone.PageNumber = pageNumber;
        baseZone.setCustomData('anchorPoint', JSON.stringify({ x: normalizedMasterX, y: normalizedMasterY }));
        baseZone.setCustomData(ManualAlignZoneCustomData.originZoneDocumentType, originZoneDocumentType);

        this.skipCropDeleteExitManualAlign = true;
        try {
          originWrapper!.annotationManager.deleteAnnotation(originZoneAnnotation, { imported: false, force: true });
        } finally {
          this.skipCropDeleteExitManualAlign = false;
        }
        originWrapper!.annotationManager.addAnnotation(baseZone);
        originWrapper!.annotationManager.redrawAnnotation(baseZone);
      }

      const linkedZoneId = baseZone.Id;
      baseZone.setCustomData(ManualAlignZoneCustomData.linkedZoneId, linkedZoneId);
      baseZone.setCustomData(ManualAlignZoneCustomData.originZoneDocumentType, originZoneDocumentType);

      // Assign zone number: preserve existing number when re-editing, otherwise allocate a new one
      let zoneNumberStr = baseZone.getCustomData(ManualAlignZoneCustomData.zoneNumber);
      if (!zoneNumberStr) {
        const currentState = store.getState();
        zoneNumberStr = `${getNextManualAlignZoneId(currentState)}`;
        baseZone.setCustomData(ManualAlignZoneCustomData.zoneNumber, zoneNumberStr);
        store.dispatch(inspection.actions.increaseManualAlignZoneId());
      }

      const TargetManualAlignZoneClass = this.createTargetManualAlignZoneAnnotation(
        overlayInstance,
        cropImageBitmap,
        sourceAnchorPoint,
        rotationQuarterTurns,
      );
      const overlayZone = new TargetManualAlignZoneClass();
      overlayZone.PageNumber = targetAnchor.pageNumber;
      overlayZone.setRect(new overlayInstance.Core.Math.Rect(overlayX1, overlayY1, overlayX2, overlayY2));
      // Store the anchor as a [0..1] fraction of the overlay's (possibly scaled) size
      // so it can be restored correctly on reload regardless of annotation dimensions.
      const overlayWidth = overlayX2 - overlayX1;
      const overlayHeight = overlayY2 - overlayY1;
      if (
        !Number.isFinite(overlayWidth) ||
        !Number.isFinite(overlayHeight) ||
        overlayWidth <= 0 ||
        overlayHeight <= 0
      ) {
        this.showSnackMessage('Failed to place manual align overlay at page edge. Please try again.', 'error');
        this.exitManualAlignMode();
        if (cropImageBitmap) {
          try {
            cropImageBitmap.close();
          } catch {
            // ignore
          }
        }
        return;
      }
      const normalizedTargetXRaw = (targetAnchor.x - overlayX1) / overlayWidth;
      const normalizedTargetYRaw = (targetAnchor.y - overlayY1) / overlayHeight;
      const normalizedTargetX = Number.isFinite(normalizedTargetXRaw)
        ? Math.max(0, Math.min(1, normalizedTargetXRaw))
        : 0.5;
      const normalizedTargetY = Number.isFinite(normalizedTargetYRaw)
        ? Math.max(0, Math.min(1, normalizedTargetYRaw))
        : 0.5;
      overlayZone.setCustomData('anchorPoint', JSON.stringify({ x: normalizedTargetX, y: normalizedTargetY }));
      overlayZone.setCustomData(ManualAlignZoneCustomData.linkedZoneId, linkedZoneId);
      overlayZone.setCustomData(ManualAlignZoneCustomData.originZoneDocumentType, originZoneDocumentType);
      overlayZone.setCustomData(ManualAlignZoneCustomData.zoneNumber, zoneNumberStr);
      overlayZone.setCustomData(ManualAlignZoneCustomData.resized, 'false');
      overlayZone.setCustomData(
        ManualAlignZoneCustomData.positionSnapshot,
        JSON.stringify({
          anchorX: overlayX1 + normalizedTargetX * overlayWidth,
          anchorY: overlayY1 + normalizedTargetY * overlayHeight,
          x: overlayX1,
          y: overlayY1,
          width: overlayWidth,
          height: overlayHeight,
        }),
      );

      overlayWrapper.annotationManager.addAnnotation(overlayZone);
      await overlayWrapper.annotationManager.drawAnnotations({ pageNumber: targetAnchor.pageNumber });

      this.cleanupAnchorPointAnnotations();
      overlayInstance.Core.annotationManager.selectAnnotation(overlayZone);
      this.exitManualAlignMode();
      setupMoveAttemptListeners();

      this.annotationMixin.updateInputAnnotations(true);
    } catch (err) {
      this.showSnackMessage('Failed to create manual align overlay. Please try again.', 'error');
      this.exitManualAlignMode();
      if (cropImageBitmap) {
        try {
          cropImageBitmap.close();
        } catch {
          // ignore
        }
      }
    }
  }

  handleManualAlignClick(documentType: DocumentTypes, e: any) {
    const SPACEBAR_RELEASE_COOLDOWN_MS = 200; // prevent accidentally placing anchor as you exit spacebar panning
    if (isPanningWithSpacebar || Date.now() - lastSpacebarReleaseTime < SPACEBAR_RELEASE_COOLDOWN_MS) return;

    const state = store.getState();
    const manualAlignMode = state.inspection.manualAlignMode;

    if (!manualAlignMode.isActive) return;

    const viewer = documentType === DocumentTypes.source ? this.source : this.target;
    if (!viewer?.instance) return;
    const scrollViewElement = viewer.instance.Core.documentViewer.getScrollViewElement();
    const clickTarget = e.target || (e.originalEvent && e.originalEvent.target);
    if (!clickTarget || !scrollViewElement?.contains(clickTarget)) return;

    const docViewer = viewer.instance.Core.documentViewer;
    const displayMode = docViewer.getDisplayModeManager().getDisplayMode();

    const scrollRect = scrollViewElement.getBoundingClientRect();
    const mouseEvent = e.originalEvent || e;
    const windowX = mouseEvent.clientX - scrollRect.left + scrollViewElement.scrollLeft;
    const windowY = mouseEvent.clientY - scrollRect.top + scrollViewElement.scrollTop;

    const windowPoint = { x: windowX, y: windowY };
    const page = displayMode.getSelectedPages(windowPoint, windowPoint).first;
    if (!page) return;

    const pageCoordinates = displayMode.windowToPage(windowPoint, page);

    if (manualAlignMode.masterAnchorPoint) {
      this.handleTargetAnchorClick(documentType, pageCoordinates, page);
    } else {
      this.handleMasterAnchorClick(documentType, pageCoordinates, page);
    }
  }

  private handleMasterAnchorClick(
    documentType: DocumentTypes,
    pageCoordinates: { x: number; y: number },
    pageNumber: number,
  ) {
    const state = store.getState();
    const manualAlignMode = state.inspection.manualAlignMode;
    const originZoneDocumentType = (manualAlignMode.originZoneDocumentType ?? DocumentTypes.source) as DocumentTypes;

    if (documentType !== originZoneDocumentType) {
      return;
    }

    if (!manualAlignMode.cropZoneId) return;
    const originWrapper = originZoneDocumentType === DocumentTypes.source ? this.source : this.target;
    const originZoneAnnotation = originWrapper?.annotationManager.getAnnotationById(manualAlignMode.cropZoneId);
    if (!originZoneAnnotation) {
      return;
    }
    if (originZoneAnnotation.PageNumber !== pageNumber) {
      return;
    }
    const zoomLevel = originWrapper!.instance.UI.getZoomLevel();
    if (!isPagePointInsideCropZone(pageCoordinates, originZoneAnnotation, zoomLevel, false)) {
      return;
    }
    const rect = originZoneAnnotation.getRect();
    const clampedX = Math.max(rect.x1, Math.min(rect.x2, pageCoordinates.x));
    const clampedY = Math.max(rect.y1, Math.min(rect.y2, pageCoordinates.y));
    const masterAnchorPoint = {
      x: clampedX,
      y: clampedY,
      pageNumber,
    };
    store.dispatch(
      inspection.actions.setAnchorPoint({
        type: 'master',
        ...masterAnchorPoint,
      }),
    );

    trackEvent({
      name: 'source-anchor-placed',
      data: {
        inspectionId: getInspectionId(store.getState()),
        anchorCoordinates: { x: clampedX, y: clampedY, pageNumber },
        cropCoordinates: { x1: rect.x1, y1: rect.y1, x2: rect.x2, y2: rect.y2 },
      },
    });

    if (originWrapper?.instance) {
      this.drawMasterAnchorPoint(originWrapper.instance, masterAnchorPoint);
    }
    originZoneAnnotation.NoMove = true;
    originZoneAnnotation.NoResize = true;
    originWrapper?.annotationManager.redrawAnnotation(originZoneAnnotation);
    const overlayDocType = OPPOSITE_DOCUMENT[originZoneDocumentType];
    const overlayWrapper = overlayDocType === DocumentTypes.source ? this.source : this.target;
    originWrapper?.removeAnnotationDocumentHighlight();
    overlayWrapper?.addDocumentHighlight();
    resetCursors();

    const { expireAllToast, setDocumentToast } = useDocumentToastStateStore.getState();
    expireAllToast();
    setDocumentToast(overlayDocType, {
      type: 'escToExit',
      message: 'Place corresponding anchor point',
      infoMessage: 'Esc to exit',
    });
  }

  private createAnchorPointAnnotation(
    instance: WebViewerInstance,
    anchorPoint: { x: number; y: number; pageNumber: number },
    customDataKey: 'masterAnchorPoint' | 'targetAnchorPoint',
  ): Core.Annotations.Annotation {
    class AnchorPointAnnotation extends GVAnnotationMixin(instance.Core.Annotations.RectangleAnnotation) {
      constructor() {
        super();
        this.setCustomData(customDataKey, 'true');
        this.ReadOnly = true;
        this.IsHoverable = false;
      }

      draw(ctx: CanvasRenderingContext2D, pageMatrix: any) {
        this.setStyles(ctx, pageMatrix);
        const centerX = this.X + this.Width / 2;
        const centerY = this.Y + this.Height / 2;
        drawAnchorIcon(ctx, centerX, centerY);
      }
    }

    const anchorAnnotation = new AnchorPointAnnotation();
    const anchorSize = 8;
    anchorAnnotation.setRect(
      new instance.Core.Math.Rect(
        anchorPoint.x - anchorSize / 2,
        anchorPoint.y - anchorSize / 2,
        anchorPoint.x + anchorSize / 2,
        anchorPoint.y + anchorSize / 2,
      ),
    );
    anchorAnnotation.PageNumber = anchorPoint.pageNumber;
    return anchorAnnotation;
  }

  private drawAnchorPoint(
    instance: WebViewerInstance,
    anchorPoint: { x: number; y: number; pageNumber: number },
    type: 'master' | 'target',
  ) {
    const originZoneDocumentType = this.currentOriginZoneDocumentType ?? DocumentTypes.source;
    const overlayDocType = OPPOSITE_DOCUMENT[originZoneDocumentType];
    const wrapper =
      type === 'master'
        ? originZoneDocumentType === DocumentTypes.source
          ? this.source
          : this.target
        : overlayDocType === DocumentTypes.source
          ? this.source
          : this.target;
    const existingAnnotation = type === 'master' ? this.masterAnchorPointAnnotation : this.targetAnchorPointAnnotation;
    const annotationProperty = type === 'master' ? 'masterAnchorPointAnnotation' : 'targetAnchorPointAnnotation';
    const customDataKey = type === 'master' ? 'masterAnchorPoint' : 'targetAnchorPoint';

    if (existingAnnotation && wrapper?.annotationManager) {
      wrapper.annotationManager.deleteAnnotation(existingAnnotation, {
        imported: false,
        force: true,
      });
      (this as any)[annotationProperty] = null;
    }
    const anchorAnnotation = this.createAnchorPointAnnotation(instance, anchorPoint, customDataKey);
    if (wrapper?.annotationManager) {
      wrapper.annotationManager.addAnnotation(anchorAnnotation);
      wrapper.annotationManager.drawAnnotationsFromList([anchorAnnotation]);
      (this as any)[annotationProperty] = anchorAnnotation;
    }
  }

  drawMasterAnchorPoint(sourceInstance: WebViewerInstance, anchorPoint: { x: number; y: number; pageNumber: number }) {
    this.drawAnchorPoint(sourceInstance, anchorPoint, 'master');
  }

  private drawTargetAnchorPoint(
    targetInstance: WebViewerInstance,
    anchorPoint: { x: number; y: number; pageNumber: number },
  ) {
    this.drawAnchorPoint(targetInstance, anchorPoint, 'target');
  }

  private handleTargetAnchorClick(
    documentType: DocumentTypes,
    pageCoordinates: { x: number; y: number },
    pageNumber: number,
  ) {
    const state = store.getState();
    const manualAlignMode = state.inspection.manualAlignMode;
    const originZoneDocumentType = (manualAlignMode.originZoneDocumentType ?? DocumentTypes.source) as DocumentTypes;
    const overlayDocumentType = OPPOSITE_DOCUMENT[originZoneDocumentType];

    if (documentType !== overlayDocumentType) {
      return;
    }

    if (!manualAlignMode.masterAnchorPoint) {
      return;
    }
    const targetAnchorPoint = {
      x: pageCoordinates.x,
      y: pageCoordinates.y,
      pageNumber,
    };
    store.dispatch(
      inspection.actions.setAnchorPoint({
        type: 'target',
        ...targetAnchorPoint,
      }),
    );

    const originWrapper = originZoneDocumentType === DocumentTypes.source ? this.source : this.target;
    const cropZoneAnnotation = manualAlignMode.cropZoneId
      ? originWrapper?.annotationManager.getAnnotationById(manualAlignMode.cropZoneId)
      : undefined;
    const cropRect = cropZoneAnnotation?.getRect();
    const masterAnchor = manualAlignMode.masterAnchorPoint;
    const overlayCropCoordinates =
      cropRect && masterAnchor
        ? {
            x1: cropRect.x1 + (pageCoordinates.x - masterAnchor.x),
            y1: cropRect.y1 + (pageCoordinates.y - masterAnchor.y),
            x2: cropRect.x2 + (pageCoordinates.x - masterAnchor.x),
            y2: cropRect.y2 + (pageCoordinates.y - masterAnchor.y),
          }
        : undefined;
    trackEvent({
      name: 'new-anchor-placed',
      data: {
        inspectionId: getInspectionId(state),
        anchorCoordinates: { x: pageCoordinates.x, y: pageCoordinates.y, pageNumber },
        cropCoordinates: overlayCropCoordinates,
      },
    });

    const overlayWrapper = overlayDocumentType === DocumentTypes.source ? this.source : this.target;
    if (overlayWrapper?.instance) {
      this.drawTargetAnchorPoint(overlayWrapper.instance, targetAnchorPoint);
    }
    this.startManualAlign(manualAlignMode.masterAnchorPoint, targetAnchorPoint);
  }

  manualAlignOverlayAnnotationSelected(documentType: DocumentTypes, annotation: Core.Annotations.Annotation) {
    const instance = documentType === DocumentTypes.source ? this.source?.instance : this.target?.instance;
    if (!instance) return;

    // Origin zone: Edit Anchors = full restart (both anchors). Overlay zone: Edit Anchor = Anchor B only.
    const zoneType = !!(annotation as ManualAlignZoneAnnotation).cleanup
      ? ManualAlignZoneType.Overlay
      : ManualAlignZoneType.Origin;
    const editPopup = this.getEditManualAlignZonePopup(instance, annotation, zoneType);
    const popupButtons = [editPopup];
    if (zoneType === ManualAlignZoneType.Overlay) {
      popupButtons.push(this.getAspectRatioTogglePopup(instance, annotation));
    }
    popupButtons.push(this.getDeleteManualAlignZonePopup(instance, annotation));
    instance.UI.annotationPopup.update(popupButtons);
  }

  manualAlignZoneAnnotationChanged(
    documentType: DocumentTypes,
    annotation: Core.Annotations.Annotation,
    action: string,
  ) {
    // Skip auto-save when annotations are being reloaded from persisted data
    const isReload =
      action === AnnotationAction.ADD && annotation.getCustomData(AnnotationCustomData.drawMarkups) === 'true';
    if (isReload) return;

    if (action === AnnotationAction.DELETE) {
      // Skip cascade when we're programmatically deleting (Edit flow or revert flow)
      if (this.skipCascadeDelete) {
        this.refreshPageOverlayForManualAlign(documentType, annotation.PageNumber);
        this.annotationMixin.updateInputAnnotations(true);
        return;
      }
      const linkedZoneId = annotation.getCustomData(ManualAlignZoneCustomData.linkedZoneId);
      const isOriginZone = linkedZoneId && annotation.Id === linkedZoneId;
      if (isOriginZone || !linkedZoneId) {
        this.deleteManualAlignZones(annotation);
      } else {
        this.revertOriginToCropZone(annotation);
      }
      this.shakeManualAlignZoneNumbers();
      this.annotationMixin.updateInputAnnotations(true);
    } else if (action === AnnotationAction.ADD || action === AnnotationAction.MODIFY) {
      if (action === AnnotationAction.MODIFY) {
        annotation.setCustomData(ManualAlignZoneCustomData.resized, 'true');

        const linkedZoneId = annotation.getCustomData(ManualAlignZoneCustomData.linkedZoneId);
        const isLinkedTarget = linkedZoneId && linkedZoneId !== annotation.Id;
        if (isLinkedTarget) {
          const snapshotStr = annotation.getCustomData(ManualAlignZoneCustomData.positionSnapshot);
          if (snapshotStr) {
            try {
              const snapshot = JSON.parse(snapshotStr);
              const anchorData = JSON.parse(annotation.getCustomData('anchorPoint') || '{}');
              const normX = typeof anchorData.x === 'number' && Number.isFinite(anchorData.x) ? anchorData.x : 0.5;
              const normY = typeof anchorData.y === 'number' && Number.isFinite(anchorData.y) ? anchorData.y : 0.5;
              if (Number.isFinite(snapshot.anchorX) && Number.isFinite(snapshot.anchorY)) {
                const currentAnchorX = annotation.X + normX * annotation.Width;
                const currentAnchorY = annotation.Y + normY * annotation.Height;
                const dx = snapshot.anchorX - currentAnchorX;
                const dy = snapshot.anchorY - currentAnchorY;
                const EPS = 1e-6;
                if (Math.abs(dx) > EPS || Math.abs(dy) > EPS) {
                  annotation.X += dx;
                  annotation.Y += dy;
                  const wrapper = PDFManagerFactory.getViewer(documentType);
                  wrapper?.annotationManager.redrawAnnotation(annotation);
                }
                annotation.setCustomData(
                  ManualAlignZoneCustomData.positionSnapshot,
                  JSON.stringify({
                    anchorX: snapshot.anchorX,
                    anchorY: snapshot.anchorY,
                    x: annotation.X,
                    y: annotation.Y,
                    width: annotation.Width,
                    height: annotation.Height,
                  }),
                );
              }
            } catch {
              /* invalid snapshot, skip */
            }
          }
        }
      }
      this.refreshPageOverlayForManualAlign(documentType, annotation.PageNumber);
      this.annotationMixin.updateInputAnnotations(true);
    }
  }

  private getAnchorCoordinatesFromZone(
    annotation: Core.Annotations.Annotation,
  ): { x: number; y: number; pageNumber: number } | undefined {
    const anchorPointData = annotation.getCustomData('anchorPoint');
    if (!anchorPointData || anchorPointData === 'none') return undefined;
    try {
      const normalized = JSON.parse(anchorPointData) as { x: number; y: number };
      const rect = annotation.getRect();
      const width = rect.x2 - rect.x1;
      const height = rect.y2 - rect.y1;
      return {
        x: rect.x1 + normalized.x * width,
        y: rect.y1 + normalized.y * height,
        pageNumber: annotation.PageNumber,
      };
    } catch {
      return undefined;
    }
  }

  private trackManualAlignDeletion(
    originZone: Core.Annotations.Annotation | undefined,
    overlayZone: Core.Annotations.Annotation | undefined,
  ): void {
    const inspectionId = getInspectionId(store.getState());
    if (originZone) {
      const anchorCoordinates = this.getAnchorCoordinatesFromZone(originZone);
      trackEvent({
        name: 'source-anchor-deleted',
        data: {
          inspectionId,
          anchorCoordinates,
        },
      });
    }
    if (overlayZone) {
      const anchorCoordinates = this.getAnchorCoordinatesFromZone(overlayZone);
      trackEvent({
        name: 'new-anchor-deleted',
        data: {
          inspectionId,
          anchorCoordinates,
        },
      });
    }
  }

  private cleanupAnnotationIfNeeded(annotation: Core.Annotations.Annotation): void {
    if ((annotation as ManualAlignZoneAnnotation).cleanup) {
      try {
        (annotation as ManualAlignZoneAnnotation).cleanup!();
      } catch {
        // ignore
      }
    }
  }

  private deleteManualAlignZones(annotation: Core.Annotations.Annotation): void {
    const isManualAlign = annotation.getCustomData(ManualAlignZoneCustomData.manualAlignZoneAnnotation) === 'true';

    if (!isManualAlign) return;

    this.cleanupAnnotationIfNeeded(annotation);

    const linkedZoneId = annotation.getCustomData(ManualAlignZoneCustomData.linkedZoneId);
    if (!linkedZoneId) return;

    const hasManualAlignAndLinkedId = (a: Core.Annotations.Annotation) =>
      a.getCustomData(ManualAlignZoneCustomData.manualAlignZoneAnnotation) === 'true' &&
      a.getCustomData(ManualAlignZoneCustomData.linkedZoneId) === linkedZoneId;

    // Collect zones for tracking before deletion
    const rawOriginDocType = annotation.getCustomData(ManualAlignZoneCustomData.originZoneDocumentType);
    const originZoneDocumentType =
      rawOriginDocType === DocumentTypes.source || rawOriginDocType === DocumentTypes.target
        ? rawOriginDocType
        : DocumentTypes.source;
    const overlayDocumentType = OPPOSITE_DOCUMENT[originZoneDocumentType];
    const overlayWrapper = overlayDocumentType === DocumentTypes.source ? this.source : this.target;
    const overlayZoneForTracking = overlayWrapper?.annotationManager
      .getAnnotationsList()
      .find(hasManualAlignAndLinkedId);
    this.trackManualAlignDeletion(annotation, overlayZoneForTracking);

    for (const doc of [this.source, this.target]) {
      if (!doc) continue;
      const documentType = doc === this.source ? DocumentTypes.source : DocumentTypes.target;
      const toDelete = doc.annotationManager.getAnnotationsList().filter(hasManualAlignAndLinkedId);
      const pagesToRefresh = new Set(toDelete.map((a) => a.PageNumber));
      for (const a of toDelete) {
        this.cleanupAnnotationIfNeeded(a);
        doc.annotationManager.deleteAnnotation(a, { imported: true, force: true });
      }
      for (const pageNumber of pagesToRefresh) {
        this.refreshPageOverlayForManualAlign(documentType, pageNumber);
      }
    }

    useDocumentToastStateStore.getState().expireAllToast();

    if (!this.hasAnyOverlayZones()) {
      teardownMoveAttemptListeners();
    }
  }

  private revertOriginToCropZone(overlayAnnotation: Core.Annotations.Annotation): void {
    const linkedZoneId = overlayAnnotation.getCustomData(ManualAlignZoneCustomData.linkedZoneId);
    const originZoneDocumentType =
      (overlayAnnotation.getCustomData(ManualAlignZoneCustomData.originZoneDocumentType) as DocumentTypes) ??
      DocumentTypes.source;

    if (!linkedZoneId) return;

    const originWrapper = originZoneDocumentType === DocumentTypes.source ? this.source : this.target;
    const originZone = originWrapper?.annotationManager.getAnnotationById(linkedZoneId);

    if (!originWrapper?.instance || !originZone) return;

    this.trackManualAlignDeletion(originZone, overlayAnnotation);

    this.cleanupAnnotationIfNeeded(overlayAnnotation);

    this.skipCascadeDelete = true;
    try {
      const cropRect = originZone.getRect();
      const pageNumber = originZone.PageNumber;

      originWrapper.annotationManager.deleteAnnotation(originZone, { imported: false, force: true });

      const docManager = PDFManagerFactory.getPDFDocManager();
      const CropZoneClass = (docManager as any).createCropZoneAnnotation?.(originWrapper.instance);
      if (!CropZoneClass) return;

      const newCropZone = new CropZoneClass();
      newCropZone.setRect(cropRect);
      newCropZone.PageNumber = pageNumber;
      newCropZone.setCustomData(AnnotationCustomData.drawMarkups, 'true');

      originWrapper.annotationManager.addAnnotation(newCropZone);
      originWrapper.instance.Core.annotationManager.bringToBack(newCropZone);
      this.refreshPageOverlayForManualAlign(originZoneDocumentType, pageNumber);
      this.refreshPageOverlayForManualAlign(OPPOSITE_DOCUMENT[originZoneDocumentType], overlayAnnotation.PageNumber);
    } finally {
      this.skipCascadeDelete = false;
    }

    useDocumentToastStateStore.getState().expireAllToast();

    if (!this.hasAnyOverlayZones()) {
      teardownMoveAttemptListeners();
    }
  }

  private hasAnyOverlayZones(): boolean {
    for (const doc of [this.source, this.target]) {
      if (!doc) continue;
      if (
        doc.annotationManager
          .getAnnotationsList()
          .some(
            (a) =>
              a.getCustomData(ManualAlignZoneCustomData.manualAlignZoneAnnotation) === 'true' &&
              a.getCustomData(ManualAlignZoneCustomData.linkedZoneId) &&
              a.getCustomData(ManualAlignZoneCustomData.linkedZoneId) !== a.Id,
          )
      )
        return true;
    }
    return false;
  }

  /**
   * Renumber all manual align zones sequentially (1, 2, 3, …) after a
   * deletion so there are no gaps. Each linked pair (identified by
   * linkedZoneId) shares the same number.
   */
  private shakeManualAlignZoneNumbers(): void {
    const isManualAlign = (a: Core.Annotations.Annotation) =>
      a.getCustomData(ManualAlignZoneCustomData.manualAlignZoneAnnotation) === 'true';

    const sourceAnnots = (this.source?.annotationManager.getAnnotationsList() || []).filter(isManualAlign);
    const targetAnnots = (this.target?.annotationManager.getAnnotationsList() || []).filter(isManualAlign);
    const allAnnots = [...sourceAnnots, ...targetAnnots];

    // Group by linkedZoneId — each group is one logical zone pair
    const groups = new Map<string, Core.Annotations.Annotation[]>();
    for (const annot of allAnnots) {
      const linkedId = annot.getCustomData(ManualAlignZoneCustomData.linkedZoneId);
      if (!linkedId) continue;
      let group = groups.get(linkedId);
      if (!group) {
        group = [];
        groups.set(linkedId, group);
      }
      group.push(annot);
    }

    // Sort groups by their current zone number (ascending)
    const sortedGroups = [...groups.values()].sort((a, b) => {
      const numA = parseInt(a[0].getCustomData(ManualAlignZoneCustomData.zoneNumber) || '0', 10);
      const numB = parseInt(b[0].getCustomData(ManualAlignZoneCustomData.zoneNumber) || '0', 10);
      return numA - numB;
    });

    // Reassign sequential numbers and redraw
    let nextNumber = 1;
    for (const group of sortedGroups) {
      const newStr = `${nextNumber}`;
      for (const annot of group) {
        annot.setCustomData(ManualAlignZoneCustomData.zoneNumber, newStr);
        const wrapper = this.source?.annotationManager.getAnnotationById(annot.Id) ? this.source : this.target;
        wrapper?.annotationManager.redrawAnnotation(annot);
      }
      nextNumber++;
    }

    store.dispatch(inspection.actions.setManualAlignZoneId(nextNumber));
  }

  private getEditManualAlignZonePopup(
    instance: WebViewerInstance,
    annotation: Core.Annotations.Annotation,
    zoneType: ManualAlignZoneType,
  ) {
    const config =
      zoneType === ManualAlignZoneType.Origin
        ? { title: 'Edit Anchors', dataElement: 'editManualAlignZoneButton' }
        : { title: 'Edit Anchor', dataElement: 'editAnchorButton' };
    return {
      type: 'actionButton',
      img: '/icons/editAnchor.svg',
      ...config,
      onClick: () => this.editManualAlignZone(annotation, zoneType),
    };
  }

  private getAspectRatioTogglePopup(instance: WebViewerInstance, annotation: Core.Annotations.Annotation) {
    const isUnlocked = isAspectRatioUnlocked(annotation);
    return {
      type: 'actionButton',
      img: isUnlocked ? '/icons/aspectRatioUnlocked.svg' : '/icons/aspectRatioLocked.svg',
      title: isUnlocked ? 'Lock aspect ratio' : 'Unlock aspect ratio',
      dataElement: isUnlocked ? 'lockAspectRatioButton' : 'unlockAspectRatioButton',
      onClick: () => {
        const unlocking = !isUnlocked;
        annotation.setCustomData(ManualAlignZoneCustomData.aspectRatioUnlocked, unlocking ? 'true' : 'false');
        // The lock button is shown only while the overlay is unlocked, so a lock event follows an unlock.
        trackEvent(
          aspectRatioToggleTrackEvent(
            unlocking,
            getInspectionId(store.getState()),
            annotation.getCustomData(ManualAlignZoneCustomData.zoneNumber),
          ),
        );
        this.annotationMixin.updateInputAnnotations(true);
        const annotationManager = instance.Core.annotationManager;
        annotationManager.deselectAnnotation(annotation);
        annotationManager.selectAnnotation(annotation);
      },
    };
  }

  private getDeleteManualAlignZonePopup(instance: WebViewerInstance, annotation: Core.Annotations.Annotation) {
    return {
      type: 'actionButton',
      img: '/icons/zoneDelete.svg',
      title: 'Delete',
      dataElement: 'deleteManualAlignZoneButton',
      onClick: () => {
        instance.Core.annotationManager.deleteAnnotation(annotation, { imported: false, force: true });
        this.exitManualAlignMode();
        instance.Core.annotationManager.deselectAllAnnotations();
        this.annotationMixin.updateInputAnnotations(true);
      },
    };
  }

  private serializeToManualAlignZone(annotation: Core.Annotations.Annotation): SerializedManualAlignZone {
    return serializeManualAlignZone(annotation);
  }

  private async restoreFromEditSnapshot(snapshot: {
    originZoneId: string;
    originAnchorPoint: string;
    overlayZones: SerializedManualAlignZone[];
  }): Promise<void> {
    if (!this.source || !this.target) return;

    if (snapshot.originAnchorPoint && snapshot.originAnchorPoint !== 'none') {
      const originAnnotation =
        this.source?.annotationManager.getAnnotationById(snapshot.originZoneId) ??
        this.target?.annotationManager.getAnnotationById(snapshot.originZoneId);
      if (originAnnotation) {
        const wrapper = this.source?.annotationManager.getAnnotationById(snapshot.originZoneId)
          ? this.source
          : this.target;
        if (wrapper) {
          originAnnotation.setCustomData('anchorPoint', snapshot.originAnchorPoint);
          wrapper.annotationManager.redrawAnnotation(originAnnotation);
        }
      }
    }

    if (snapshot.overlayZones.length === 0) return;

    const baseZone =
      this.source.annotationManager.getAnnotationById(snapshot.overlayZones[0].linkedZoneId) ??
      this.target.annotationManager.getAnnotationById(snapshot.overlayZones[0].linkedZoneId);
    if (!baseZone) return;

    const baseZoneSerialized = this.serializeToManualAlignZone(baseZone);
    const originZoneDocumentType = baseZoneSerialized.originZoneDocumentType ?? DocumentTypes.source;

    const toInputAnnotation = (zone: SerializedManualAlignZone): InputAnnotation => ({
      annotationId: zone.annotationId,
      page: zone.pageNumber,
      manualAlignZone: zone,
    });

    const sourceManualAlignZones: InputAnnotation[] = [];
    const targetManualAlignZones: InputAnnotation[] = [];

    if (originZoneDocumentType === DocumentTypes.source) {
      sourceManualAlignZones.push(toInputAnnotation(baseZoneSerialized));
    } else {
      targetManualAlignZones.push(toInputAnnotation(baseZoneSerialized));
    }

    for (const overlayZone of snapshot.overlayZones) {
      const overlayDocType = overlayZone.originZoneDocumentType
        ? OPPOSITE_DOCUMENT[overlayZone.originZoneDocumentType]
        : OPPOSITE_DOCUMENT[originZoneDocumentType];
      const input = toInputAnnotation(overlayZone);
      if (overlayDocType === DocumentTypes.source) {
        sourceManualAlignZones.push(input);
      } else {
        targetManualAlignZones.push(input);
      }
    }

    await this.reloadManualAlignZones(sourceManualAlignZones, targetManualAlignZones);
    this.annotationMixin.updateInputAnnotations(true);
  }

  /**
   * Fetch a page image blob for a given page number from the specified document's file.
   */
  private async fetchPageImageForPage(
    pageNumber: number,
    documentType: DocumentTypes = DocumentTypes.source,
  ): Promise<{ blob: Blob; dpiUsed: number }> {
    const state = store.getState();
    const file = state.files[documentType].file;
    const response = await axiosInstanceQueries.get(`files/${file.fileId}/page-image-url?page=${pageNumber}`);
    const imageResponse = await fetch(response.data.url);
    if (!imageResponse.ok) {
      throw new Error(`Failed to fetch image: ${imageResponse.status} ${imageResponse.statusText}`);
    }
    const blob = await imageResponse.blob();
    const dpiUsed: number = response.data.dpiUsed ?? 300;
    return { blob, dpiUsed };
  }

  /**
   * Reload manual align zones from saved InputAnnotation data.
   * Base zones are created synchronously; overlay zones are created
   * asynchronously because the overlay image must be fetched from the server.
   */
  async reloadManualAlignZones(sourceManualAlignZones: InputAnnotation[], targetManualAlignZones: InputAnnotation[]) {
    if (!this.source || !this.target) return;

    // Build base zone lookup: linkedZoneId -> base zone (base zone has annotationId === linkedZoneId)
    const baseZoneByLinkedId = new Map<string, { zone: SerializedManualAlignZone; documentType: DocumentTypes }>();
    const overlayZones: { zone: SerializedManualAlignZone; documentType: DocumentTypes }[] = [];

    const processMarkups = (markups: InputAnnotation[], documentType: DocumentTypes) => {
      for (const markup of markups) {
        const zone = markup.manualAlignZone;
        if (!zone) continue;
        if (zone.annotationId === zone.linkedZoneId) {
          baseZoneByLinkedId.set(zone.linkedZoneId, { zone, documentType });
        } else {
          overlayZones.push({ zone, documentType });
        }
      }
    };
    processMarkups(sourceManualAlignZones, DocumentTypes.source);
    processMarkups(targetManualAlignZones, DocumentTypes.target);

    // --- Reload base zones (synchronous, no image needed) ---
    for (const { zone, documentType } of baseZoneByLinkedId.values()) {
      const wrapper = documentType === DocumentTypes.source ? this.source : this.target;
      if (!wrapper) continue;
      const existingAnnotation = wrapper.annotationManager.getAnnotationById(zone.annotationId);
      if (existingAnnotation) continue;

      const SourceClass = this.createSourceManualAlignZoneAnnotation(wrapper.instance);
      const annotation = new SourceClass();
      annotation.Id = zone.annotationId;
      annotation.PageNumber = zone.pageNumber;
      annotation.setRect(new wrapper.instance.Core.Math.Rect(zone.x1, zone.y1, zone.x2, zone.y2));
      annotation.setCustomData('anchorPoint', JSON.stringify(zone.anchorPoint));
      annotation.setCustomData(ManualAlignZoneCustomData.linkedZoneId, zone.linkedZoneId);
      annotation.setCustomData(AnnotationCustomData.drawMarkups, 'true');
      const originZoneDocumentType = zone.originZoneDocumentType ?? documentType;
      annotation.setCustomData(ManualAlignZoneCustomData.originZoneDocumentType, originZoneDocumentType);
      if (zone.zoneNumber != null) {
        annotation.setCustomData(ManualAlignZoneCustomData.zoneNumber, `${zone.zoneNumber}`);
      }

      wrapper.annotationManager.addAnnotation(annotation);
      wrapper.annotationManager.redrawAnnotation(annotation);
      this.refreshPageOverlayForManualAlign(documentType, zone.pageNumber, false);
    }

    // --- Reload overlay zones (async — overlay images must be fetched) ---
    // Group by (originZoneDocumentType, pageNumber) for batch fetching
    const overlaysByFetchKey = new Map<
      string,
      { overlayZone: SerializedManualAlignZone; overlayDocType: DocumentTypes; baseZone: SerializedManualAlignZone }[]
    >();
    for (const { zone: overlayZone, documentType: overlayDocType } of overlayZones) {
      const baseEntry = baseZoneByLinkedId.get(overlayZone.linkedZoneId);
      if (!baseEntry) continue;
      const { zone: baseZone, documentType: baseDocType } = baseEntry;
      // Base zone's document is the crop document (where the image is from)
      const originZoneDocumentType = baseZone.originZoneDocumentType ?? baseDocType;
      const fetchKey = `${originZoneDocumentType}-${baseZone.pageNumber}`;
      if (!overlaysByFetchKey.has(fetchKey)) {
        overlaysByFetchKey.set(fetchKey, []);
      }
      overlaysByFetchKey.get(fetchKey)!.push({ overlayZone, overlayDocType, baseZone });
    }

    for (const [fetchKey, entries] of overlaysByFetchKey) {
      const [originDocType, pageStr] = fetchKey.split('-');
      const originZoneDocumentType = originDocType as DocumentTypes;
      const pageNumber = parseInt(pageStr, 10);
      try {
        const { blob: imageBlob, dpiUsed } = await this.fetchPageImageForPage(pageNumber, originZoneDocumentType);

        for (const { overlayZone, overlayDocType, baseZone } of entries) {
          const overlayWrapper = overlayDocType === DocumentTypes.source ? this.source : this.target;
          if (!overlayWrapper) continue;
          try {
            if (overlayWrapper.annotationManager.getAnnotationById(overlayZone.annotationId)) continue;

            const cropWidth = baseZone.x2 - baseZone.x1;
            const cropHeight = baseZone.y2 - baseZone.y1;
            const scaleFactor = dpiUsed / 72;

            const imageX = baseZone.x1 * scaleFactor;
            const imageY = baseZone.y1 * scaleFactor;
            const imageWidthPixels = cropWidth * scaleFactor;
            const imageHeightPixels = cropHeight * scaleFactor;

            const cropImageBitmap = await createImageBitmap(
              imageBlob,
              imageX,
              imageY,
              imageWidthPixels,
              imageHeightPixels,
            );

            const rotationQuarterTurns = getOverlayRotationQuarterTurns(
              (baseZone.originZoneDocumentType ?? DocumentTypes.source) === DocumentTypes.source
                ? this.source!.instance
                : this.target!.instance,
              baseZone.pageNumber,
              overlayWrapper.instance,
              overlayZone.pageNumber,
            );

            const TargetClass = this.createTargetManualAlignZoneAnnotation(
              overlayWrapper.instance,
              cropImageBitmap,
              baseZone.anchorPoint,
              rotationQuarterTurns,
            );
            const annotation = new TargetClass();
            annotation.Id = overlayZone.annotationId;
            annotation.PageNumber = overlayZone.pageNumber;
            annotation.setRect(
              new overlayWrapper.instance.Core.Math.Rect(
                overlayZone.x1,
                overlayZone.y1,
                overlayZone.x2,
                overlayZone.y2,
              ),
            );
            annotation.setCustomData('anchorPoint', JSON.stringify(overlayZone.anchorPoint));
            annotation.setCustomData(ManualAlignZoneCustomData.linkedZoneId, overlayZone.linkedZoneId);
            annotation.setCustomData(ManualAlignZoneCustomData.originZoneDocumentType, originZoneDocumentType);
            annotation.setCustomData(AnnotationCustomData.drawMarkups, 'true');
            if (overlayZone.zoneNumber != null) {
              annotation.setCustomData(ManualAlignZoneCustomData.zoneNumber, `${overlayZone.zoneNumber}`);
            }
            if (overlayZone.resized) {
              annotation.setCustomData(ManualAlignZoneCustomData.resized, 'true');
            }
            const unlockedValue = aspectRatioUnlockedDataValue(overlayZone.aspectRatioUnlocked);
            if (unlockedValue) {
              annotation.setCustomData(ManualAlignZoneCustomData.aspectRatioUnlocked, unlockedValue);
            }
            const reloadWidth = overlayZone.x2 - overlayZone.x1;
            const reloadHeight = overlayZone.y2 - overlayZone.y1;
            annotation.setCustomData(
              ManualAlignZoneCustomData.positionSnapshot,
              JSON.stringify({
                anchorX: overlayZone.x1 + (overlayZone.anchorPoint.x ?? 0.5) * reloadWidth,
                anchorY: overlayZone.y1 + (overlayZone.anchorPoint.y ?? 0.5) * reloadHeight,
                x: overlayZone.x1,
                y: overlayZone.y1,
                width: reloadWidth,
                height: reloadHeight,
              }),
            );

            overlayWrapper.annotationManager.addAnnotation(annotation);
            overlayWrapper.annotationManager.redrawAnnotation(annotation);
            this.refreshPageOverlayForManualAlign(overlayDocType, overlayZone.pageNumber, false);
          } catch (err) {
            console.error('Failed to reload manual align overlay zone:', err);
          }
        }
      } catch (err) {
        console.error(
          `Failed to fetch page image for page ${pageNumber} (${originZoneDocumentType}) during manual align zone reload:`,
          err,
        );
      }
    }

    // Update the Redux counter to be above the max zone number loaded,
    // but never regress it (restoreFromEditSnapshot may reload a subset).
    const allZones = [...sourceManualAlignZones, ...targetManualAlignZones];
    let maxZoneNumber = 0;
    for (const markup of allZones) {
      const zn = markup.manualAlignZone?.zoneNumber;
      if (zn != null && zn > maxZoneNumber) {
        maxZoneNumber = zn;
      }
    }
    const currentNextId = getNextManualAlignZoneId(store.getState());
    if (maxZoneNumber + 1 > currentNextId) {
      store.dispatch(inspection.actions.setManualAlignZoneId(maxZoneNumber + 1));
    }

    // Refresh views if any zones were loaded
    if (sourceManualAlignZones.length > 0 && this.source) {
      this.source.instance.Core.documentViewer.refreshAll();
      this.source.instance.Core.documentViewer.updateView();
    }
    if (targetManualAlignZones.length > 0 && this.target) {
      this.target.instance.Core.documentViewer.refreshAll();
      this.target.instance.Core.documentViewer.updateView();
    }

    if (overlayZones.length > 0) {
      setupMoveAttemptListeners();
    }
  }

  private editManualAlignZone(annotation: Core.Annotations.Annotation, zoneType: ManualAlignZoneType) {
    if (zoneType === ManualAlignZoneType.Origin) {
      const linkedZoneId = annotation.getCustomData(ManualAlignZoneCustomData.linkedZoneId) || annotation.Id;
      const originZoneDocumentType =
        (annotation.getCustomData(ManualAlignZoneCustomData.originZoneDocumentType) as DocumentTypes) ??
        DocumentTypes.source;

      const overlayDocType = OPPOSITE_DOCUMENT[originZoneDocumentType];
      const overlayWrapper = overlayDocType === DocumentTypes.source ? this.source : this.target;

      const hasLinkedOverlay = (a: Core.Annotations.Annotation) =>
        a.getCustomData(ManualAlignZoneCustomData.manualAlignZoneAnnotation) === 'true' &&
        a.getCustomData(ManualAlignZoneCustomData.linkedZoneId) === linkedZoneId &&
        a.Id !== linkedZoneId;

      const overlaysToDelete = overlayWrapper
        ? overlayWrapper.annotationManager.getAnnotationsList().filter(hasLinkedOverlay)
        : [];
      const overlayZonesSnapshot = overlaysToDelete.map((a) => this.serializeToManualAlignZone(a));
      const originAnchorPoint = annotation.getCustomData('anchorPoint') || '';

      this.editModeRestoreSnapshot = {
        originZoneId: annotation.Id,
        originAnchorPoint,
        overlayZones: overlayZonesSnapshot,
      };

      if (overlayWrapper) {
        this.skipCascadeDelete = true;
        try {
          const pagesToRefresh = new Set(overlaysToDelete.map((a) => a.PageNumber));
          for (const overlay of overlaysToDelete) {
            this.cleanupAnnotationIfNeeded(overlay);
            overlayWrapper.annotationManager.deleteAnnotation(overlay, { imported: false, force: true });
          }
          for (const pageNumber of pagesToRefresh) {
            this.refreshPageOverlayForManualAlign(overlayDocType, pageNumber);
          }
        } finally {
          this.skipCascadeDelete = false;
        }
      }

      const originWrapper = originZoneDocumentType === DocumentTypes.source ? this.source : this.target;
      if (!originWrapper?.instance) {
        this.showSnackMessage('Origin zone document is not available.', 'error');
        return;
      }

      // Clear the old anchor so it doesn't show until the user places the new one
      annotation.setCustomData('anchorPoint', 'none');
      originWrapper.annotationManager.redrawAnnotation(annotation);

      originWrapper.instance.Core.annotationManager.deselectAllAnnotations();
      this.enterManualAlignMode(originWrapper.instance, annotation, originZoneDocumentType);
      this.annotationMixin.updateInputAnnotations(true);
    } else {
      const baseZoneId =
        annotation.getCustomData(ManualAlignZoneCustomData.linkedZoneId) ||
        annotation.getCustomData('sourceCropZoneId'); // legacy
      if (!baseZoneId) {
        this.showSnackMessage('Unable to edit anchor: missing source information.', 'error');
        return;
      }

      const baseZone =
        this.source?.annotationManager.getAnnotationById(baseZoneId) ??
        this.target?.annotationManager.getAnnotationById(baseZoneId);
      if (!baseZone) {
        this.showSnackMessage('Base zone not found.', 'error');
        return;
      }

      const originZoneDocumentType =
        (baseZone.getCustomData(ManualAlignZoneCustomData.originZoneDocumentType) as DocumentTypes) ??
        DocumentTypes.source;

      let masterAnchorPoint: { x: number; y: number; pageNumber: number };
      const masterAnchorPointData = annotation.getCustomData('masterAnchorPoint');
      if (masterAnchorPointData) {
        try {
          masterAnchorPoint = JSON.parse(masterAnchorPointData);
        } catch {
          this.showSnackMessage('Unable to edit anchor: invalid anchor point data.', 'error');
          return;
        }
      } else {
        const anchorPointData = baseZone.getCustomData('anchorPoint');
        if (!anchorPointData || anchorPointData === 'none') {
          this.showSnackMessage('Unable to edit anchor: missing anchor point.', 'error');
          return;
        }
        try {
          const anchorPoint = JSON.parse(anchorPointData);
          const rect = baseZone.getRect();
          masterAnchorPoint = {
            x: rect.x1 + anchorPoint.x * (rect.x2 - rect.x1),
            y: rect.y1 + anchorPoint.y * (rect.y2 - rect.y1),
            pageNumber: baseZone.PageNumber,
          };
        } catch {
          this.showSnackMessage('Unable to edit anchor: invalid anchor point data.', 'error');
          return;
        }
      }

      const overlayZonesSnapshot = [this.serializeToManualAlignZone(annotation)];

      this.editModeRestoreSnapshot = {
        originZoneId: baseZone.Id,
        originAnchorPoint: '', // Origin unchanged in overlay edit
        overlayZones: overlayZonesSnapshot,
      };

      if ((annotation as ManualAlignZoneAnnotation).cleanup) {
        try {
          (annotation as ManualAlignZoneAnnotation).cleanup!();
        } catch {
          // ignore
        }
      }
      const overlayDocumentType = OPPOSITE_DOCUMENT[originZoneDocumentType];
      const overlayWrapper = overlayDocumentType === DocumentTypes.source ? this.source : this.target;
      const overlayInstance = overlayWrapper?.instance;
      if (overlayInstance) {
        this.skipCascadeDelete = true;
        try {
          overlayInstance.Core.annotationManager.deleteAnnotation(annotation, { imported: false, force: true });
        } finally {
          this.skipCascadeDelete = false;
        }
      }

      const originWrapper = originZoneDocumentType === DocumentTypes.source ? this.source : this.target;
      this.enterManualAlignMode(originWrapper!.instance!, baseZone, originZoneDocumentType);
      store.dispatch(
        inspection.actions.setAnchorPoint({
          type: 'master',
          x: masterAnchorPoint.x,
          y: masterAnchorPoint.y,
          pageNumber: masterAnchorPoint.pageNumber,
        }),
      );
      if (originWrapper?.instance) {
        this.drawMasterAnchorPoint(originWrapper.instance, masterAnchorPoint);
      }
      originWrapper?.removeAnnotationDocumentHighlight();
      overlayWrapper?.addDocumentHighlight();
      const { expireAllToast, setDocumentToast } = useDocumentToastStateStore.getState();
      expireAllToast();
      setDocumentToast(overlayDocumentType, {
        type: 'escToExit',
        message: 'Place corresponding anchor point',
        infoMessage: 'Esc to exit',
      });
    }
  }
}

export default ManualAlignZone;
